import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  memo,
} from 'react';
import {
  makeStyles,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  RemoveCircle,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { Button, useToast } from '@redshelf/redshelf-ui';
import {
  CollaborationModifiersContext,
  CollaborationValuesContext,
} from 'contexts';
import IconButton from 'components/IconButton';
import { IconTooltip } from 'components';
import { UnsubscribeConfirm } from 'components/Collaboration';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      margin: theme.spacing(3, 2),
    },
    viewNSubscribeText: {
      color: theme.palette.greyBlue[700],
    },
    subscribeForm: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: theme.spacing(2, 0),
    },
    codeInputField: {
      '&.MuiTextField-root': {
        margin: theme.spacing(0, 2, 0, 0),
        flexGrow: 1,
      },
      '& .MuiOutlinedInput-root': {
        color: theme.palette.greyBlue[700],
        height: theme.spacing(5),

        '& fieldset': {
          borderColor: `rgba(0, 0, 0, 0.23)`,
        },
        '&:hover fieldset': {
          borderColor: `rgba(0, 0, 0, 0.23)`,
        },
        '&.Mui-focused fieldset': {
          borderColor: ({ inputError }) =>
            inputError ? '#D32F2F' : theme.palette.shelfBlue[500],
        },
      },
      '& label.Mui-focused': {
        color: ({ inputError }) =>
          inputError ? '#D32F2F' : theme.palette.greyBlue[600],
      },
      '& .MuiOutlinedInput-root.Mui-error': {
        '&:hover fieldset': {
          borderColor: '#D32F2F',
        },
      },
    },
    inputLabel: {
      color: theme.palette.greyBlue[600],
      fontSize: theme.spacing(2),
      top: theme.spacing(-1),
      '&.MuiInputLabel-shrink': {
        top: 0,
      },
    },
    submitCodeBtn: {
      height: theme.spacing(5),
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(-0.75),
    },
    errorIcon: {
      marginRight: theme.spacing(1),
      color: '#D32F2F',
      fontSize: theme.spacing(2),
    },
    errorMessage: {
      fontSize: theme.spacing(1.5),
      color: '#D32F2F',
    },
    subscribeListTitle: {
      padding: theme.spacing(2, 0),
    },
    subscription: {
      padding: theme.spacing(1, 0),
    },
    listTitle: { color: theme.palette.greyBlue[700], display: 'flex' },
    listActionWrapper: {
      display: 'flex',
      gap: theme.spacing(1),
      position: 'unset',
      right: 'unset',
      top: 'unset',
      transform: 'unset',
    },
    tooltip: {
      textAlign: 'center',
    },
    hidden: {
      display: 'none',
    },
  };
});

const Subscription = memo(({ subscription, setSelectedSubscription }) => {
  const classes = useStyles();
  const { enqueueToast } = useToast();
  const { isPaused, sharer } = subscription;
  const { pauseSubscription, resumeSubscription } = useContext(
    CollaborationModifiersContext,
  );

  const [isWaiting, setIsWaiting] = useState(false);

  const onToggleSubscriptionIconClick = async () => {
    if (isWaiting) {
      return;
    }

    setIsWaiting(true);

    isPaused
      ? await resumeSubscription(subscription)
      : await pauseSubscription(subscription);

    setIsWaiting(false);
    enqueueToast(
      `Now ${isPaused ? 'showing' : 'hiding'} notes for ${sharer.name}`,
      { variant: 'success' },
    );
  };

  const onUnsubscribeIconClick = () => setSelectedSubscription(subscription);

  const subscriptionText = (
    <span>
      <span className="noTranslate">{sharer.namePossessed}</span> Notes
    </span>
  );

  const toggleNotesLabel = (
    <p className={classes.tooltip}>
      {isPaused ? 'Show' : 'Hide'}
      <span className="noTranslate"> {sharer.namePossessed} </span>Notes
    </p>
  );

  const unsubscribeLabel = (
    <p className={classes.tooltip}>
      Unsubscribe from
      <span className="noTranslate">
        {' '}
        {sharer.firstName} {sharer.lastName}
      </span>
    </p>
  );

  const buttonsJSX = (
    <>
      <div id={`toggle-notes-label-${sharer.id}`} className={classes.hidden}>
        {toggleNotesLabel}
      </div>
      <IconTooltip
        disabled={isWaiting}
        id="toggle-notes"
        title={toggleNotesLabel}
      >
        <IconButton
          aria-labelledby={`toggle-notes-label-${sharer.id}`}
          disabled={isWaiting}
          id={`toggle-${sharer.id}`}
          onClick={onToggleSubscriptionIconClick}
        >
          {isPaused ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </IconTooltip>

      <div id={`unsubscribe-label-${sharer.id}`} className={classes.hidden}>
        {unsubscribeLabel}
      </div>
      <IconTooltip
        disabled={isWaiting}
        id="unsubscribe"
        title={unsubscribeLabel}
      >
        <IconButton
          aria-labelledby={`unsubscribe-label-${sharer.id}`}
          disabled={isWaiting}
          id={`unsubscribe-${sharer.id}`}
          onClick={onUnsubscribeIconClick}
        >
          <RemoveCircle />
        </IconButton>
      </IconTooltip>
    </>
  );

  return (
    <>
      <ListItemText className={classes.listTitle} primary={subscriptionText} />
      <ListItemSecondaryAction className={classes.listActionWrapper}>
        {buttonsJSX}
      </ListItemSecondaryAction>
    </>
  );
});

const ViewAndSubscribe = ({ ...props }) => {
  const { subscriptions } = useContext(CollaborationValuesContext);
  const { addSubscription, removeSubscription } = useContext(
    CollaborationModifiersContext,
  );

  const [subscriptionCode, setSubscriptionCode] = useState('');
  const [hasInvalidCode, setHasInvalidCode] = useState(false);
  const [isCodeAlreadyInUse, setIsCodeAlreadyInUse] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const classes = useStyles({ inputError: hasInvalidCode });

  const { enqueueToast } = useToast();
  const codeTextFieldRef = useRef(null);

  const onCodeChange = (e) => {
    setHasInvalidCode(false);
    setIsCodeAlreadyInUse(false);
    setSubscriptionCode(e.target.value);
  };

  const onCodeSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!(await addSubscription(subscriptionCode))) {
        setHasInvalidCode(true);
        setIsCodeAlreadyInUse(
          subscriptions.some(
            (subscription) => subscription.code === subscriptionCode,
          ),
        );

        // Focus back to the input field for correction
        codeTextFieldRef.current?.focus();

        return;
      }
      setSubscriptionCode('');
      setShowSuccessToast(true);
    },
    [addSubscription, subscriptionCode, subscriptions],
  );

  useEffect(() => {
    if (showSuccessToast) {
      // Last in the list is the one recently added.
      // If name is empty or just whitespaces, no need of punctuations.
      const sharerName =
        subscriptions[subscriptions.length - 1]?.sharer.namePossessed;

      const TOAST_MESSAGE = (
        <span>
          You've successfully subscribed to{' '}
          <span className="noTranslate">{sharerName}</span> notes.
        </span>
      );
      enqueueToast(TOAST_MESSAGE, {
        key: subscriptions[subscriptions.length - 1]?.pk,
        variant: 'success',
      });

      setShowSuccessToast(false);
    }
  }, [enqueueToast, showSuccessToast, subscriptions]);

  const errorMsgJSX = hasInvalidCode ? (
    <div aria-live="assertive" className={classes.errorContainer}>
      <ErrorIcon className={classes.errorIcon} />
      <Typography className={classes.errorMessage}>
        {isCodeAlreadyInUse ? 'Code Already In Use' : 'Code Does Not Exist'}
      </Typography>
    </div>
  ) : null;

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const unsubscribeDialogCancel = useCallback(() => {
    setSelectedSubscription(null);
  }, []);

  const unsubscribeDialogConfirm = useCallback(async () => {
    await removeSubscription(selectedSubscription);

    setToastMessage(
      <span>
        You have successfully unsubscribed from{' '}
        <span className="noTranslate">
          {selectedSubscription?.sharer?.namePossessed ?? ''}
        </span>{' '}
        notes.
      </span>,
    );
    setSelectedSubscription(null);
  }, [selectedSubscription, removeSubscription]);

  useEffect(
    function enqueueUnsubscribeToast() {
      if (!toastMessage) return;

      setTimeout(() => {
        enqueueToast(toastMessage, {
          variant: 'success',
        });
      }, 1000);

      setToastMessage(null);
    },
    [enqueueToast, toastMessage],
  );

  return (
    <>
      <div className={classes.root} {...props}>
        <Typography className={classes.viewNSubscribeText} variant="body1">
          To subscribe and view another person’s notes, enter their unique,
          generated code below. Shared notes will be available until they stop
          sharing or you unsubscribe.
        </Typography>
        <form className={classes.subscribeForm} onSubmit={onCodeSubmit}>
          <TextField
            aria-label="Enter Subscription Code"
            autoComplete="off"
            className={classes.codeInputField}
            error={hasInvalidCode}
            FormHelperTextProps={{ component: 'div' }}
            helperText={errorMsgJSX}
            id="codeTextField"
            inputRef={codeTextFieldRef}
            InputLabelProps={{ className: classes.inputLabel }}
            label="Sharing Code"
            onChange={onCodeChange}
            value={subscriptionCode}
            variant="outlined"
          />
          <Button
            aria-label="Submit Subscription Code"
            className={classes.submitCodeBtn}
            type="submit"
          >
            Subscribe
          </Button>
        </form>
        {subscriptions.length ? (
          <List
            subheader={
              <Typography
                className={classes.subscribeListTitle}
                component="h3"
                variant="subtitle2"
              >
                Your Access to Other Notes:
              </Typography>
            }
          >
            {subscriptions.map((subscription) => (
              <ListItem key={subscription?.pk} className={classes.subscription}>
                <Subscription
                  subscription={subscription}
                  setSelectedSubscription={setSelectedSubscription}
                />
              </ListItem>
            ))}
          </List>
        ) : null}
      </div>
      <UnsubscribeConfirm
        subscription={selectedSubscription}
        onCancel={unsubscribeDialogCancel}
        onConfirm={unsubscribeDialogConfirm}
      />
    </>
  );
};

export default ViewAndSubscribe;
